import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { LIVE_FRAME_URL, getLiveStatus } from "@/lib/api";
import { useContent } from "@/lib/content";
import { Overline } from "@/components/Overline";

const ease = [0.16, 1, 0.3, 1];

export const LiveKitchen = () => {
  const c = useContent();
  const live = c.liveCam || {};
  const [active, setActive] = useState(false);
  const [src, setSrc] = useState("");
  const imgFailed = useRef(false);

  useEffect(() => {
    if (!live.enabled) return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await getLiveStatus();
        if (!stopped) setActive(!!s.active);
      } catch {
        if (!stopped) setActive(false);
      }
    };
    poll();
    const id = setInterval(poll, 6000);
    return () => { stopped = true; clearInterval(id); };
  }, [live.enabled]);

  useEffect(() => {
    if (!active) return;
    const refresh = () => setSrc(`${LIVE_FRAME_URL}?t=${Date.now()}`);
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [active]);

  if (!live.enabled || !active) return null;

  return (
    <section className="py-20 md:py-28 bg-ink text-cream grain" data-testid="live-kitchen-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <Overline light className="!flex justify-center">Live</Overline>
        <h2 className="font-serif font-light text-4xl sm:text-5xl mb-4">{live.title}</h2>
        <p className="text-cream/75 font-light max-w-xl mx-auto mb-10">{live.text}</p>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }}
          className="relative inline-block w-full max-w-3xl overflow-hidden rounded-xl border border-gold/30 shadow-2xl bg-ink-soft">
          <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-wine/90 text-cream text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" /> Live
          </span>
          {src && (
            <img
              src={src}
              alt="Live aus der Küche"
              data-testid="live-kitchen-img"
              className="w-full aspect-video object-cover"
              onError={() => { imgFailed.current = true; setActive(false); }}
            />
          )}
        </motion.div>
        <p className="text-xs text-cream/40 mt-4 flex items-center justify-center gap-2">
          <Flame size={14} className="text-gold" /> Aktualisiert sich automatisch · ohne Ton · keine Aufzeichnung
        </p>
      </div>
    </section>
  );
};
